import { type FC, useEffect, useMemo, useState } from 'react';
import AppLayout from '@/components/feature/AppLayout';
import { Drawer, EmptyState, FilterBar, LoadingState, SearchInput, useToast } from '@/components/base';
import { useLegalProducts } from '@/hooks/useLegalProducts';
import {
  type CreateMessageTemplateInput,
  type MessageTemplate,
  useMessageTemplates,
} from '@/hooks/useMessageTemplates';
import { useGmailIntegration } from '@/hooks/useGmailIntegration';
import { useWhatsAppIntegration } from '@/hooks/useWhatsAppIntegration';
import { fetchWithRetry } from '@/lib/apiRequest';

type MessageTemplateFormState = {
  title: string;
  channel: string;
  category: string;
  legalProductId: string;
  content: string;
  variablesText: string;
  notes: string;
  isActive: boolean;
};

const channelOptions = [
  { label: 'WhatsApp', value: 'whatsapp' },
  { label: 'E-mail', value: 'email' },
  { label: 'Telefone', value: 'telefone' },
  { label: 'Presencial', value: 'presencial' },
  { label: 'Interno', value: 'interno' },
  { label: 'Outro', value: 'outro' },
];

const channelLabels: Record<string, string> = Object.fromEntries(
  channelOptions.map((option) => [option.value, option.label])
);

const categoryOptions = [
  { label: 'Triagem', value: 'triagem' },
  { label: 'Primeiro atendimento', value: 'primeiro_atendimento' },
  { label: 'Follow-up', value: 'follow_up' },
  { label: 'Envio de documentos', value: 'envio_documentos' },
  { label: 'Proposta de honorários', value: 'proposta_honorarios' },
  { label: 'Cobrança', value: 'cobranca' },
  { label: 'Audiência', value: 'audiencia' },
  { label: 'Encerramento', value: 'encerramento' },
  { label: 'Pós-atendimento', value: 'pos_atendimento' },
  { label: 'Operacional', value: 'operacional' },
  { label: 'Outro', value: 'outro' },
];

const categoryLabels: Record<string, string> = Object.fromEntries(
  categoryOptions.map((option) => [option.value, option.label])
);

const emptyFormState = (): MessageTemplateFormState => ({
  title: '',
  channel: 'whatsapp',
  category: 'triagem',
  legalProductId: '',
  content: '',
  variablesText: '',
  notes: '',
  isActive: true,
});

const toNullableText = (value: string) => {
  const text = value.trim();
  return text === '' ? null : text;
};

const normalizeSearchText = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

const formatDateTime = (value: string | null | undefined) => {
  if (!value) {
    return '—';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const truncateText = (value: string, size = 160) =>
  value.length > size ? `${value.slice(0, size).trimEnd()}…` : value;

const splitVariables = (value: string | null) =>
  (value ?? '')
    .split(/[\n,;]+/g)
    .map((item) => item.trim())
    .filter(Boolean);

const messageTemplateToFormState = (template: MessageTemplate): MessageTemplateFormState => ({
  title: template.title,
  channel: template.channel || 'whatsapp',
  category: template.category || 'triagem',
  legalProductId: template.legalProductId ?? '',
  content: template.content,
  variablesText: template.variables ?? '',
  notes: template.notes ?? '',
  isActive: template.isActive,
});

const buildPayload = (form: MessageTemplateFormState): CreateMessageTemplateInput => ({
  title: form.title.trim(),
  channel: form.channel,
  category: toNullableText(form.category),
  legalProductId: toNullableText(form.legalProductId),
  content: form.content.trim(),
  variables: toNullableText(form.variablesText),
  notes: toNullableText(form.notes),
  isActive: form.isActive,
});

const MessagesPage: FC = () => {
  const { addToast } = useToast();
  const {
    messageTemplates,
    loading,
    error,
    refetch,
    createMessageTemplate,
    updateMessageTemplate,
    archiveMessageTemplate,
  } = useMessageTemplates();
  const { legalProducts } = useLegalProducts();
  const { gmail, isLoadingGmail } = useGmailIntegration();
  const { whatsapp, isLoadingWhatsApp } = useWhatsAppIntegration();

  const [search, setSearch] = useState('');
  const [channelFilter, setChannelFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [productFilter, setProductFilter] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<MessageTemplate | null>(null);
  const [form, setForm] = useState<MessageTemplateFormState>(emptyFormState());
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);
  const [archivingTemplateId, setArchivingTemplateId] = useState<string | null>(null);

  const legalProductOptions = useMemo(
    () => legalProducts.map((product) => ({ label: product.name, value: product.id })),
    [legalProducts]
  );

  const filteredTemplates = useMemo(() => {
    const query = normalizeSearchText(search);

    return messageTemplates.filter((template) => {
      if (!template.isActive) {
        return false;
      }

      if (channelFilter && template.channel !== channelFilter) {
        return false;
      }

      if (categoryFilter && template.category !== categoryFilter) {
        return false;
      }

      if (productFilter && template.legalProductId !== productFilter) {
        return false;
      }

      if (!query) {
        return true;
      }

      const haystack = normalizeSearchText(
        [
          template.title,
          template.channel,
          template.category,
          template.legalProductName,
          template.variables,
          template.notes,
          template.content,
        ]
          .filter(Boolean)
          .join(' ')
      );

      return haystack.includes(query);
    });
  }, [categoryFilter, channelFilter, messageTemplates, productFilter, search]);

  const selectedTemplate = useMemo(
    () => messageTemplates.find((template) => template.id === selectedTemplateId) ?? null,
    [messageTemplates, selectedTemplateId]
  );

  useEffect(() => {
    if (filteredTemplates.length === 0) {
      if (selectedTemplateId !== null) {
        setSelectedTemplateId(null);
      }
      return;
    }

    if (!selectedTemplateId || !filteredTemplates.some((template) => template.id === selectedTemplateId)) {
      setSelectedTemplateId(filteredTemplates[0].id);
    }
  }, [filteredTemplates, selectedTemplateId]);

  const openCreateDrawer = () => {
    if (isSavingTemplate) {
      return;
    }

    setEditingTemplate(null);
    setForm(emptyFormState());
    setEditorOpen(true);
  };

  const openEditDrawer = (template: MessageTemplate) => {
    if (isSavingTemplate) {
      return;
    }

    setEditingTemplate(template);
    setForm(messageTemplateToFormState(template));
    setEditorOpen(true);
  };

  const closeEditor = () => {
    if (isSavingTemplate) {
      return;
    }

    setEditorOpen(false);
    setEditingTemplate(null);
    setForm(emptyFormState());
  };

  const saveTemplate = async () => {
    if (isSavingTemplate) {
      return;
    }

    if (!form.title.trim()) {
      addToast('Preencha o título da mensagem.', 'warning');
      return;
    }

    if (!form.content.trim()) {
      addToast('Preencha o conteúdo da mensagem.', 'warning');
      return;
    }

    setIsSavingTemplate(true);

    try {
      const payload = buildPayload(form);
      const saved = editingTemplate
        ? await updateMessageTemplate(editingTemplate.id, payload)
        : await createMessageTemplate(payload);

      setSelectedTemplateId(saved.id);
      addToast(editingTemplate ? 'Mensagem atualizada com sucesso.' : 'Mensagem criada com sucesso.', 'success');
      closeEditor();
    } catch (saveError: any) {
      addToast(saveError?.message || 'Não foi possível salvar a mensagem.', 'error');
    } finally {
      setIsSavingTemplate(false);
    }
  };

  const archiveTemplate = async (template: MessageTemplate) => {
    if (isSavingTemplate || archivingTemplateId) {
      return;
    }

    const confirmed = window.confirm(
      'Tem certeza que deseja arquivar esta mensagem? Esta ação removerá a mensagem da listagem principal, mas manterá o registro no banco para auditoria.'
    );

    if (!confirmed) {
      return;
    }

    setArchivingTemplateId(template.id);

    try {
      await archiveMessageTemplate(template.id);
      if (selectedTemplateId === template.id) {
        setSelectedTemplateId(null);
      }
      addToast('Mensagem arquivada com sucesso.', 'success');
    } catch (archiveError: any) {
      addToast(archiveError?.message || 'Não foi possível arquivar a mensagem.', 'error');
    } finally {
      setArchivingTemplateId(null);
    }
  };

  const copyTemplateContent = async (template: MessageTemplate) => {
    try {
      await navigator.clipboard.writeText(template.content);
      addToast('Conteúdo copiado para a área de transferência.', 'success');
    } catch {
      addToast('Não foi possível copiar o conteúdo.', 'error');
    }
  };

  const refreshTemplates = () => {
    void refetch();
  };

  const sendTemplateThroughGmail = async (template: MessageTemplate) => {
    if (template.channel !== 'email') {
      addToast('Este modelo não está configurado para o canal E-mail.', 'warning');
      return;
    }

    if (!gmail?.sendEnabled) {
      addToast('Conecte o Gmail em Integrações antes de enviar.', 'warning');
      return;
    }

    const recipient = window.prompt('E-mail do destinatário:')?.trim();
    if (!recipient) {
      return;
    }

    const confirmed = window.confirm(`Enviar "${template.title}" para ${recipient}?`);
    if (!confirmed) {
      return;
    }

    try {
      const response = await fetchWithRetry('/api/communications/send', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Idempotency-Key': crypto.randomUUID(),
        },
        body: JSON.stringify({
          to: recipient,
          subject: template.title,
          body: template.content,
          templateId: template.id,
        }),
      });

      const data = await response.json() as { success?: boolean; error?: string };
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Não foi possível enviar o e-mail.');
      }

      addToast('E-mail enviado pelo Gmail e registrado no histórico.', 'success');
    } catch (sendError) {
      addToast(sendError instanceof Error ? sendError.message : 'Não foi possível enviar o e-mail.', 'error');
    }
  };

  const sendTemplateThroughWhatsApp = async (template: MessageTemplate) => {
    if (template.channel !== 'whatsapp') {
      addToast('Este modelo não está configurado para o canal WhatsApp.', 'warning');
      return;
    }

    if (!whatsapp?.sendConfigured) {
      addToast('Configure a WhatsApp Cloud API em Integrações antes de enviar.', 'warning');
      return;
    }

    const recipient = window.prompt('WhatsApp do destinatário com DDD:')?.trim();
    if (!recipient) {
      return;
    }

    const useMetaTemplate = window.confirm(
      'Clique em OK para enviar um TEMPLATE META aprovado.\n\nClique em Cancelar para enviar mensagem livre dentro da janela de atendimento de 24 horas.'
    );

    let payload: Record<string, unknown>;

    if (useMetaTemplate) {
      const metaTemplateName = window.prompt('Nome exato do template aprovado na Meta:')?.trim();

      if (!metaTemplateName) {
        return;
      }

      const metaTemplateLanguage =
        window.prompt('Código do idioma do template:', 'pt_BR')?.trim() || 'pt_BR';

      const rawParameters =
        window.prompt(
          'Parâmetros do corpo, na ordem, separados por ponto e vírgula. Deixe vazio se não houver:',
          ''
        ) ?? '';

      payload = {
        to: recipient,
        mode: 'template',
        templateId: template.id,
        metaTemplateName,
        metaTemplateLanguage,
        metaTemplateParameters: rawParameters
          .split(';')
          .map((item) => item.trim())
          .filter(Boolean),
      };
    } else {
      const confirmed = window.confirm(
        `Enviar "${template.title}" para ${recipient} como mensagem livre?\n\nIsso só funciona se o cliente tiver enviado mensagem nas últimas 24 horas.`
      );

      if (!confirmed) {
        return;
      }

      payload = {
        to: recipient,
        mode: 'text',
        body: template.content,
        templateId: template.id,
      };
    }

    try {
      const response = await fetchWithRetry('/api/communications/whatsapp-send', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Idempotency-Key': crypto.randomUUID(),
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json() as {
        success?: boolean;
        error?: string;
      };

      if (!response.ok || !data.success) {
        if (data.error === 'whatsapp_template_required') {
          throw new Error(
            'A janela de atendimento de 24 horas está fechada. Use um template aprovado pela Meta.'
          );
        }

        throw new Error(
          data.error ||
          'Não foi possível enviar o WhatsApp.'
        );
      }

      addToast(
        'WhatsApp enviado pela Cloud API e registrado no histórico.',
        'success'
      );
    } catch (sendError) {
      addToast(
        sendError instanceof Error
          ? sendError.message
          : 'Não foi possível enviar o WhatsApp.',
        'error'
      );
    }
  };

  return (
    <AppLayout>
      <div className="p-4 md:p-6 space-y-5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h2 className="text-xl font-bold text-foreground-900">Central de Mensagens</h2>
            <p className="text-sm text-foreground-500 mt-0.5">
              Mensagens prontas internas para atendimento, cobrança e follow-up
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={refreshTemplates}
              className="inline-flex items-center gap-1.5 px-3 py-2 border border-background-200 text-foreground-600 rounded-lg text-xs font-semibold hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-refresh-line"></i> Atualizar
            </button>
            <button
              onClick={openCreateDrawer}
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-primary-500 text-background-50 rounded-lg text-xs font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line"></i> Nova Mensagem
            </button>
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Buscar mensagem..."
            className="w-full sm:max-w-sm"
          />

          <FilterBar
            filters={[
              {
                key: 'channel',
                label: 'Canal',
                options: channelOptions,
                value: channelFilter,
                onChange: setChannelFilter,
              },
              {
                key: 'category',
                label: 'Categoria',
                options: categoryOptions,
                value: categoryFilter,
                onChange: setCategoryFilter,
              },
              {
                key: 'product',
                label: 'Produto jurídico',
                options: legalProductOptions,
                value: productFilter,
                onChange: setProductFilter,
              },
            ]}
          />
        </div>

        {error && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 flex items-start gap-2">
            <i className="ri-alert-line mt-0.5"></i>
            <div className="flex-1">
              <p className="font-medium">Não foi possível carregar todas as mensagens reais.</p>
              <p className="text-amber-700 mt-0.5">{error}</p>
            </div>
            <button
              onClick={refreshTemplates}
              className="text-xs font-semibold px-2.5 py-1.5 rounded-lg border border-amber-200 bg-white hover:bg-amber-100 transition-colors"
            >
              Tentar novamente
            </button>
          </div>
        )}

        {loading && messageTemplates.length === 0 ? (
          <LoadingState message="Carregando mensagens prontas reais..." />
        ) : filteredTemplates.length === 0 ? (
          <EmptyState
            icon="ri-message-3-line"
            title="Nenhuma mensagem encontrada"
            description="Cadastre uma nova mensagem pronta ou ajuste os filtros para localizar registros existentes."
            action={{
              label: 'Nova Mensagem',
              onClick: openCreateDrawer,
              icon: 'ri-add-line',
            }}
          />
        ) : (
          <div className="grid gap-4 lg:grid-cols-[minmax(0,1.45fr)_minmax(340px,0.95fr)]">
            <div className="rounded-2xl border border-background-200 bg-background-50 shadow-sm">
              <div className="flex items-center justify-between px-4 py-3 border-b border-background-200">
                <div>
                  <h3 className="text-sm font-semibold text-foreground-900">Mensagens salvas</h3>
                  <p className="text-xs text-foreground-500">
                    {filteredTemplates.length} registro{filteredTemplates.length === 1 ? '' : 's'} visível{filteredTemplates.length === 1 ? '' : 'eis'}
                  </p>
                </div>
                <span className="text-[11px] font-semibold px-2 py-1 rounded-full bg-primary-50 text-primary-700 border border-primary-100">
                  Arquivadas ocultas
                </span>
              </div>

              <div className="p-4 space-y-3">
                {filteredTemplates.map((template) => {
                  const channelLabel = channelLabels[template.channel] ?? template.channel;
                  const categoryLabel = categoryLabels[template.category ?? ''] ?? template.category ?? 'Outro';
                  const isSelected = selectedTemplateId === template.id;

                  return (
                    <div
                      key={template.id}
                      onClick={() => setSelectedTemplateId(template.id)}
                      className={`group rounded-xl border p-4 transition-all cursor-pointer ${
                        isSelected
                          ? 'border-primary-300 bg-primary-50/40 shadow-sm'
                          : 'border-background-200 bg-white hover:border-primary-200 hover:shadow-sm'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <h4 className="text-sm font-semibold text-foreground-900 truncate">{template.title}</h4>
                          <p className="mt-1 text-xs text-foreground-500 leading-relaxed">
                            {truncateText(template.content, 180)}
                          </p>
                        </div>
                        <span className="flex-none px-2 py-1 rounded-full text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                          Ativa
                        </span>
                      </div>

                      <div className="mt-3 flex flex-wrap gap-1.5">
                        <span className="px-2 py-1 rounded-full text-[10px] font-medium bg-background-100 text-foreground-600 border border-background-200">
                          {channelLabel}
                        </span>
                        <span className="px-2 py-1 rounded-full text-[10px] font-medium bg-background-100 text-foreground-600 border border-background-200">
                          {categoryLabel}
                        </span>
                        {template.legalProductName && (
                          <span className="px-2 py-1 rounded-full text-[10px] font-medium bg-primary-50 text-primary-700 border border-primary-100">
                            {template.legalProductName}
                          </span>
                        )}
                      </div>

                      <div className="mt-4 flex flex-wrap items-center gap-2">
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            openEditDrawer(template);
                          }}
                          className="px-2.5 py-1.5 rounded-lg text-xs font-medium border border-background-200 text-foreground-600 hover:bg-background-100 transition-colors"
                        >
                          <i className="ri-edit-line mr-1"></i>Editar
                        </button>
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            void archiveTemplate(template);
                          }}
                          disabled={archivingTemplateId === template.id}
                          className="px-2.5 py-1.5 rounded-lg text-xs font-medium border border-amber-200 text-amber-700 hover:bg-amber-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          <i className="ri-archive-line mr-1"></i>
                          {archivingTemplateId === template.id ? 'Arquivando...' : 'Arquivar'}
                        </button>
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            void copyTemplateContent(template);
                          }}
                          className="px-2.5 py-1.5 rounded-lg text-xs font-medium border border-background-200 text-foreground-600 hover:bg-background-100 transition-colors"
                        >
                          <i className="ri-file-copy-line mr-1"></i>Copiar
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="rounded-2xl border border-background-200 bg-background-50 shadow-sm">
              <div className="flex items-center justify-between px-4 py-3 border-b border-background-200">
                <div>
                  <h3 className="text-sm font-semibold text-foreground-900">Detalhe da mensagem</h3>
                  <p className="text-xs text-foreground-500">Visualize, copie ou edite a mensagem selecionada</p>
                </div>
                {selectedTemplate && (
                  <span
                    className={`px-2 py-1 rounded-full text-[10px] font-semibold border ${
                      selectedTemplate.isActive
                        ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                        : 'bg-gray-100 text-gray-500 border-gray-200'
                    }`}
                  >
                    {selectedTemplate.isActive ? 'Ativa' : 'Arquivada'}
                  </span>
                )}
              </div>

              {selectedTemplate ? (
                <div className="p-4 space-y-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h4 className="text-base font-semibold text-foreground-900">{selectedTemplate.title}</h4>
                      <p className="text-xs text-foreground-500 mt-1">
                        {channelLabels[selectedTemplate.channel] ?? selectedTemplate.channel} ·{' '}
                        {categoryLabels[selectedTemplate.category ?? ''] ?? selectedTemplate.category ?? 'Outro'}
                      </p>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => openEditDrawer(selectedTemplate)}
                        className="px-2.5 py-1.5 rounded-lg text-xs font-medium border border-background-200 text-foreground-600 hover:bg-background-100 transition-colors"
                      >
                        <i className="ri-edit-line mr-1"></i>Editar
                      </button>
                      <button
                        type="button"
                        onClick={() => void archiveTemplate(selectedTemplate)}
                        disabled={archivingTemplateId === selectedTemplate.id}
                        className="px-2.5 py-1.5 rounded-lg text-xs font-medium border border-amber-200 text-amber-700 hover:bg-amber-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        <i className="ri-archive-line mr-1"></i>
                        {archivingTemplateId === selectedTemplate.id ? 'Arquivando...' : 'Arquivar'}
                      </button>
                      {selectedTemplate.channel === 'email' && (
                        <button
                          type="button"
                          onClick={() => void sendTemplateThroughGmail(selectedTemplate)}
                          disabled={isLoadingGmail || !gmail?.sendEnabled}
                          className="px-2.5 py-1.5 rounded-lg text-xs font-medium bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title={gmail?.sendEnabled ? 'Enviar pelo Gmail' : 'Conecte o Gmail em Integrações'}
                        >
                          <i className="ri-mail-send-line mr-1"></i>Enviar Gmail
                        </button>
                      )}
                      {selectedTemplate.channel === 'whatsapp' && (
                        <button
                          type="button"
                          onClick={() => void sendTemplateThroughWhatsApp(selectedTemplate)}
                          disabled={isLoadingWhatsApp || !whatsapp?.sendConfigured}
                          className="px-2.5 py-1.5 rounded-lg text-xs font-medium bg-emerald-600 text-white hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title={whatsapp?.sendConfigured ? 'Enviar pela WhatsApp Cloud API' : 'Configure o WhatsApp em Integrações'}
                        >
                          <i className="ri-whatsapp-line mr-1"></i>Enviar WhatsApp
                        </button>
                      )}                      <button
                        type="button"
                        onClick={() => void copyTemplateContent(selectedTemplate)}
                        className="px-2.5 py-1.5 rounded-lg text-xs font-medium border border-background-200 text-foreground-600 hover:bg-background-100 transition-colors"
                      >
                        <i className="ri-file-copy-line mr-1"></i>Copiar
                      </button>
                    </div>
                  </div>

                  {selectedTemplate.legalProductName && (
                    <div className="rounded-xl border border-primary-100 bg-primary-50/50 px-3 py-2 text-sm text-primary-800">
                      Produto jurídico: <span className="font-semibold">{selectedTemplate.legalProductName}</span>
                    </div>
                  )}

                  <div>
                    <p className="text-xs font-semibold text-foreground-500 uppercase tracking-wide mb-2">Conteúdo</p>
                    <div className="rounded-xl border border-background-200 bg-white px-3 py-3 text-sm text-foreground-700 whitespace-pre-wrap leading-relaxed">
                      {selectedTemplate.content}
                    </div>
                  </div>

                  <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                      <p className="text-xs font-semibold text-foreground-500 uppercase tracking-wide mb-2">
                        Variáveis
                      </p>
                      <div className="flex flex-wrap gap-1.5 rounded-xl border border-background-200 bg-white px-3 py-3">
                        {splitVariables(selectedTemplate.variables).length > 0 ? (
                          splitVariables(selectedTemplate.variables).map((variable) => (
                            <span
                              key={variable}
                              className="px-2 py-1 rounded-md text-[10px] font-mono font-medium bg-background-100 text-primary-600 border border-background-200 whitespace-nowrap"
                            >
                              {variable}
                            </span>
                          ))
                        ) : (
                          <span className="text-sm text-foreground-400">Nenhuma variável informada.</span>
                        )}
                      </div>
                    </div>

                    <div>
                      <p className="text-xs font-semibold text-foreground-500 uppercase tracking-wide mb-2">
                        Observações
                      </p>
                      <div className="rounded-xl border border-background-200 bg-white px-3 py-3 text-sm text-foreground-700 min-h-[110px]">
                        {selectedTemplate.notes || '—'}
                      </div>
                    </div>
                  </div>

                  <div className="grid gap-3 sm:grid-cols-2 text-xs text-foreground-500">
                    <div className="rounded-xl border border-background-200 bg-white px-3 py-2">
                      <span className="font-semibold text-foreground-600">Criada em:</span>{' '}
                      {formatDateTime(selectedTemplate.createdAt)}
                    </div>
                    <div className="rounded-xl border border-background-200 bg-white px-3 py-2">
                      <span className="font-semibold text-foreground-600">Atualizada em:</span>{' '}
                      {formatDateTime(selectedTemplate.updatedAt)}
                    </div>
                  </div>
                </div>
              ) : (
                <EmptyState
                  icon="ri-message-3-line"
                  title="Selecione uma mensagem"
                  description="Clique em uma mensagem da lista para visualizar os detalhes, copiar o conteúdo ou abrir a edição."
                  action={{
                    label: 'Nova Mensagem',
                    onClick: openCreateDrawer,
                    icon: 'ri-add-line',
                  }}
                />
              )}
            </div>
          </div>
        )}

        <Drawer
          isOpen={editorOpen}
          onClose={closeEditor}
          title={editingTemplate ? `Editar: ${editingTemplate.title}` : 'Nova Mensagem'}
          width="w-[620px]"
          footer={
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={closeEditor}
                className="px-3 py-1.5 rounded-lg text-xs font-medium border border-background-200 text-foreground-600 hover:bg-background-100 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => void saveTemplate()}
                disabled={isSavingTemplate}
                className="px-3 py-1.5 rounded-lg text-xs font-medium bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {isSavingTemplate ? 'Salvando...' : 'Salvar Mensagem'}
              </button>
            </div>
          }
        >
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1">Título *</label>
              <input
                type="text"
                value={form.title}
                onChange={(event) => setForm((previous) => ({ ...previous, title: event.target.value }))}
                className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Canal</label>
                <select
                  value={form.channel}
                  onChange={(event) => setForm((previous) => ({ ...previous, channel: event.target.value }))}
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300 cursor-pointer"
                >
                  {channelOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Categoria</label>
                <select
                  value={form.category}
                  onChange={(event) => setForm((previous) => ({ ...previous, category: event.target.value }))}
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300 cursor-pointer"
                >
                  {categoryOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1">Produto jurídico</label>
              <select
                value={form.legalProductId}
                onChange={(event) => setForm((previous) => ({ ...previous, legalProductId: event.target.value }))}
                className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300 cursor-pointer"
              >
                <option value="">Sem vínculo</option>
                {legalProductOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1">Conteúdo *</label>
              <textarea
                value={form.content}
                onChange={(event) => setForm((previous) => ({ ...previous, content: event.target.value }))}
                rows={8}
                className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 font-mono resize-none focus:outline-none focus:border-primary-300"
                placeholder="Olá, {{clientName}}. Recebemos seu contato..."
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1">Variáveis</label>
              <textarea
                value={form.variablesText}
                onChange={(event) => setForm((previous) => ({ ...previous, variablesText: event.target.value }))}
                rows={3}
                className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 font-mono resize-none focus:outline-none focus:border-primary-300"
                placeholder="clientName, caseTitle, currentDate"
              />
              <p className="mt-1 text-[11px] text-foreground-400">
                Use variáveis manuais simples. Elas são salvas como texto e podem ser copiadas junto com a mensagem.
              </p>
            </div>

            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1">Observações</label>
              <textarea
                value={form.notes}
                onChange={(event) => setForm((previous) => ({ ...previous, notes: event.target.value }))}
                rows={3}
                className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 resize-none focus:outline-none focus:border-primary-300"
                placeholder="Observações internas"
              />
            </div>

            <label className="flex items-center gap-2 text-sm text-foreground-700">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(event) => setForm((previous) => ({ ...previous, isActive: event.target.checked }))}
                className="w-4 h-4 rounded border-background-300 text-primary-500 focus:ring-primary-300"
              />
              Mensagem ativa
            </label>
          </div>
        </Drawer>
      </div>
    </AppLayout>
  );
};

export default MessagesPage;
